import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  FormLabel,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Paper,
  InputLabel,
  Select,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, setHours, setMinutes, startOfWeek, startOfMonth, endOfMonth, endOfWeek, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios from '../../utils/axios';
import { isFrenchPublicHoliday } from '../../utils/dates';
import { getImageUrl } from '../../utils/helpers';
import ParticipantsList from '../../components/admin/ParticipantsList';
import ImageGalleryManager from '../../components/admin/ImageGalleryManager';
import useActivityTypes from '../../hooks/useActivityTypes';
import { buildCalendarEvent } from '../../utils/activity';

// Setup the localizer for react-big-calendar
const locales = {
  'fr': fr
};

const localizer = dateFnsLocalizer({
  format: (date, formatStr) => {
    if (formatStr === 'P') {
      return format(date, "'Semaine du' d MMMM", { locale: fr });
    }
    return format(date, formatStr, { locale: fr });
  },
  parse: (str) => new Date(str),
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay: (date) => date.getDay(),
  locales,
  messages: {
    previous: 'Précédent',
    next: 'Suivant',
    today: 'Aujourd\'hui',
    month: 'Mois',
    week: 'Semaine',
    day: 'Jour',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Heure',
    event: 'Événement',
  },
});

// Fallback category colours if API types unavailable
const FALLBACK_CATEGORY_COLORS = {
  'Yoga Ashtanga': '#032D23', // Dark Green
  'Hatha Yoga': '#B8860B', // Dark Yellow
  'Yoga Vinyasa': '#404616', // Bright Green
  'Yoga Restauratif': '#452663', 
  'Yoga des Énergies': '#4A601C',// Blue Violet
  'Yin Yoga': '#CF5200', // Deep Purple
  'Shiatsu': '#890857', // Dark Red
  'Chi Nei Tsang': '#B75312', // Orange Red
  'Soin': '#792720', // Deep Blue
  'Ateliers': '#0F52BA', // Indigo
  'Pilates Doux': '#6D2348', // Indigo
  'Power Pilates': '#432C5F', // Purple
  'Pilates - Tous niveaux': '#6D2348', // Same color as Pilates Doux
  'Autres': '#00533E' // Gold
};

// Keep alias so existing references compile until component-level override
const categoryColors = FALLBACK_CATEGORY_COLORS;

// Custom styles for the calendar
const calendarStyles = {
  '.rbc-time-slot': {
    height: '35px',
    '@media (max-width: 768px)': {
      height: '30px'
    }
  },
  '.rbc-label': {
    fontSize: 'var(--time-slot-font-size, 0.85rem)',
    padding: '4px',
    minWidth: '150px',
    '@media (max-width: 768px)': {
      padding: '2px',
      minWidth: '40px',
      fontSize: '0.7rem'
    }
  },
  '.rbc-time-content': {
    fontSize: 'var(--time-slot-font-size, 0.85rem)',
    '@media (max-width: 768px)': {
      fontSize: '0.7rem'
    }
  },
  '.rbc-time-header-cell': {
    fontSize: '0.85rem',
    '@media (max-width: 768px)': {
      fontSize: '0.7rem'
    }
  },
  '.rbc-time-view': {
    fontSize: '0.85rem',
    height: '700px',
    width: '100%',
    '@media (max-width: 768px)': {
      fontSize: '0.7rem',
      height: '500px'
    }
  },
  '.rbc-timeslot-group': {
    minHeight: '35px',
    '@media (max-width: 768px)': {
      minHeight: '30px'
    }
  },
  '.rbc-time-column': {
    minWidth: '2000px',
    '@media (max-width: 768px)': {
      minWidth: 'auto'
    }
  },
  '.rbc-day-slot': {
    minWidth: '2000px',
    '@media (max-width: 768px)': {
      minWidth: 'auto'
    }
  },
  '.rbc-event': {
    backgroundColor: 'transparent',
    border: 'none'
  },
  '.rbc-event-content': {
    whiteSpace: 'pre-wrap',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    '@media (max-width: 768px)': {
      lineHeight: 1.2,
      fontSize: '0.75rem'
    },
    lineHeight: 1.3,
    fontSize: '0.8rem',
    maxHeight: '5.2em',
    overflow: 'hidden'
  },
  '.rbc-events-container': {
    marginRight: '0 !important',
    height: '100%',
    width: '100%',
    position: 'relative'
  },
  '.rbc-day-slot .rbc-events-container': {
    marginRight: '0 !important',
    position: 'relative',
    width: '100%'
  },
  '.rbc-day-slot .rbc-event': {
    position: 'absolute !important',
    width: '100% !important',
    left: '0 !important',
    right: '0 !important',
    height: '100% !important',
    margin: '0 !important',
    padding: '12px',
    '@media (max-width: 768px)': {
      padding: '8px'
    }
  },
  '.rbc-event-overlaps': {
    width: '50% !important',
    zIndex: 2
  },
  '.rbc-event-continues-after': {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0
  },
  '.rbc-day-slot .rbc-events-container .rbc-event.rbc-selected': {
    background: 'none'
  },
  '.rbc-event.rbc-selected': {
    backgroundColor: 'inherit',
    outline: 'none'
  },
  '.rbc-event:focus': {
    outline: 'none'
  },
  '.rbc-day-slot .rbc-events-container .rbc-event.rbc-event-overlaps': {
    width: 'calc(50% - 10px)',
    '&:first-of-type': {
      left: '5px'
    },
    '&:last-of-type': {
      left: 'calc(50% + 5px)'
    }
  },
  '.rbc-day-slot .rbc-background-event': {
    opacity: 0.75
  },
  '.rbc-day-slot .rbc-event-continues-before': {
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0
  },
  '.rbc-day-slot .rbc-event-continues-earlier': {
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0
  },
  '.rbc-day-slot .rbc-event-continues-later': {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0
  }
};

// Add getVisibleRange helper function
const getVisibleRange = (date, view) => {
  let start, end;
  
  switch (view) {
    case 'month':
      start = startOfMonth(date);
      end = endOfMonth(date);
      break;
    case 'week':
      start = startOfWeek(date, { weekStartsOn: 1 });
      end = endOfWeek(date, { weekStartsOn: 1 });
      break;
    default: // day view
      start = startOfDay(date);
      end = endOfDay(date);
  }
  
  return { start, end };
};

/**
 * @typedef {Object} Event
 * @property {string} title
 * @property {Date} start
 * @property {Date} end
 * @property {number} maxParticipants
 * @property {Array} participants
 */

/**
 * Renders an event in the calendar
 * @param {Object} props
 * @param {Event} props.event
 * @returns {JSX.Element}
 */
const EventComponent = ({ event }) => {
  const { t } = useTranslation();
  const maxParticipants = event.maxParticipants || 1;
  const participantCount = event.currentParticipants || 0;
  const availableSpots = maxParticipants - participantCount;
  const isIndividualSession = ['Shiatsu', 'Chi Nei Tsang', 'Soin'].includes(event.category);

  return (
    <div className="event-content">
      <div className="event-title">{event.title?.split('\n')[0] || event.category}</div>
      <div className="event-time">
        {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
      </div>
      <div className="event-availability">
        {availableSpots === 0 ? (
          <span className="no-spots">{t('noAvailableSpots')}</span>
        ) : (
          <span className="spots-available">
            {isIndividualSession ? 
              `${availableSpots} ${t('available')}` : 
              `${participantCount}/${maxParticipants}`
            }
          </span>
        )}
      </div>
    </div>
  );
};

const ActivityManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    types,
    names: typeNames,
    byName: typesByName,
    displayNames: apiDisplayNames,
    colors: apiColors,
    ready: typesReady,
  } = useActivityTypes();
  const categoryColors = typesReady
    ? { ...FALLBACK_CATEGORY_COLORS, ...apiColors }
    : FALLBACK_CATEGORY_COLORS;
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [calendarView, setCalendarView] = useState('week');
  const [visibleRange, setVisibleRange] = useState(() => {
    const today = new Date();
    const start = startOfWeek(today, { weekStartsOn: 1 });
    const end = endOfWeek(today, { weekStartsOn: 1 });
    return { start, end };
  });
  const defaultLocation = {
    name: 'La Yoga Yourte du Luberon',
    address: '243 A Chemin des Baïsses,  84 360 Mérindol',
    city: 'Mérindol - Dans le GPS: « La yoga yourte du Luberon »'
  };
  const [currentDate, setCurrentDate] = useState(new Date());
  const [visibleActivities, setVisibleActivities] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    selectedDay: '',
    status: 'upcoming'
  });
  const [participantsDialog, setParticipantsDialog] = useState({
    open: false,
    activity: null,
    participants: []
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Update current date when activities are loaded
  useEffect(() => {
    if (activities.length > 0 && currentDate.getTime() === new Date().getTime()) {
      const now = new Date();
      const futureActivities = activities
        .filter(activity => new Date(activity.date) >= now)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      if (futureActivities.length > 0) {
        setCurrentDate(new Date(futureActivities[0].date));
      }
    }
  }, [activities, currentDate]);

  // Find the next scheduled activity date
  const getDefaultDate = () => {
    if (!activities.length) return new Date();
    
    const now = new Date();
    const futureActivities = activities
      .filter(activity => new Date(activity.date) >= now)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return futureActivities.length > 0 
      ? new Date(futureActivities[0].date)
      : new Date();
  };

  // Add function to ensure we always start from Monday of the week
  const getStartOfWeek = (date) => {
    const today = new Date(date);
    return startOfWeek(today, { weekStartsOn: 1 }); // 1 represents Monday
  };

  // Update the onNavigate handler
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
    
    let start, end;
    
    switch (calendarView) {
      case 'month':
        start = startOfMonth(newDate);
        end = endOfMonth(newDate);
        break;
      case 'week':
        start = startOfWeek(newDate, { weekStartsOn: 1 });
        end = endOfWeek(newDate, { weekStartsOn: 1 });
        break;
      default: // day view
        start = startOfDay(newDate);
        end = endOfDay(newDate);
    }

    setVisibleRange({ start, end });
  };

  const getDefaultMaxParticipants = (category) => {
    const fromApi = typesByName[category];
    if (fromApi?.defaultMaxParticipants) return String(fromApi.defaultMaxParticipants);
    const yogaTypes = ['Yoga Ashtanga', 'Hatha Yoga', 'Yoga Vinyasa', 'Yoga Restauratif', 'Yin Yoga', 'Pilates Doux', 'Power Pilates', 'Pilates - Tous niveaux', 'Yoga des Énergies'];
    if (yogaTypes.includes(category)) {
      return '10';
    }
    return category === 'Shiatsu' || category === 'Chi Nei Tsang' || category === 'Soin' ? '1' : '10';
  };

  const getDuration = (category) => {
    const fromApi = typesByName[category];
    if (fromApi?.defaultDuration) return String(fromApi.defaultDuration);
    const yogaTypes = ['Yoga Ashtanga', 'Hatha Yoga', 'Yoga Vinyasa', 'Yoga Restauratif', 'Yin Yoga', 'Pilates Doux', 'Power Pilates', 'Pilates - Tous niveaux', 'Yoga des Énergies'];
    if (yogaTypes.includes(category)) {
      return '60';
    }
    if (category === 'Shiatsu') return '70';
    if (category === 'Chi Nei Tsang' || category === 'Soin') return '90';
    return '60';
  };

  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    description: '',
    date: null,
    duration: '60',
    maxParticipants: '10',
    location: defaultLocation,
    category: '',
    image: null,
    imageUrl: null,
    featured: false,
    repeatWeekly: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [pageTab, setPageTab] = useState(0);

  const columns = [
    { field: 'title', headerName: t('title'), flex: 1 },
    { field: 'category', headerName: t('category'), width: 130 },
    {
      field: 'date',
      headerName: t('date'),
      width: 100,
      valueFormatter: (params) =>
        format(new Date(params.value), 'dd/MM'),
    },
    {
      field: 'time',
      headerName: t('time'),
      width: 100,
      valueGetter: (params) =>
        format(new Date(params.row.date), 'HH:mm'),
    },
    {
      field: 'status',
      headerName: t('status'),
      width: 130,
      valueGetter: (params) =>
        new Date(params.row.date) < new Date() ? t('past') : t('upcoming'),
    },
    {
      field: 'image',
      headerName: t('image'),
      width: 100,
      renderCell: (params) => {
        const imageUrl = params.row.images?.[0]?.url;
        if (!imageUrl) return null;
        
        const fullImageUrl = getImageUrl(imageUrl);
        
        return (
          <img
            src={fullImageUrl}
            alt=""
            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title={t('viewParticipants')}>
            <IconButton
              size="small"
              onClick={() => fetchParticipants(params.row)}
              color="primary"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('edit')}>
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('delete')}>
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  // Update getVisibleActivities to include status filter only for DataGrid
  const getVisibleActivities = React.useMemo(() => () => {
    // For calendar view - only filter by visible range and category/day
    const calendarActivities = activities.filter(activity => {
      const activityDate = new Date(activity.date);
      const isInVisibleRange = activityDate >= visibleRange.start && activityDate <= visibleRange.end;
      const matchesCategory = !filters.category || activity.category === filters.category;
      const matchesDay = !filters.selectedDay || format(activityDate, 'yyyy-MM-dd') === filters.selectedDay;
      return isInVisibleRange && matchesCategory && matchesDay;
    });

    // For DataGrid view - apply all filters including status
    const gridActivities = activities.filter(activity => {
      const activityDate = new Date(activity.date);
      const now = new Date();
      const matchesCategory = !filters.category || activity.category === filters.category;
      const matchesDay = !filters.selectedDay || format(activityDate, 'yyyy-MM-dd') === filters.selectedDay;
      const matchesStatus = !filters.status || 
        (filters.status === 'upcoming' && activityDate >= now) ||
        (filters.status === 'past' && activityDate < now) ||
        filters.status === 'all';
      return matchesCategory && matchesDay && matchesStatus;
    });

    return {
      calendarActivities,
      gridActivities
    };
  }, [activities, visibleRange, filters]);

  // Update useEffect to handle both sets of activities
  useEffect(() => {
    const filtered = getVisibleActivities();
    setVisibleActivities(filtered.calendarActivities);
  }, [calendarView, visibleRange, activities, filters, getVisibleActivities]);

  // Add useEffect for initial data fetch
  useEffect(() => {
    fetchActivities();
  }, []); // Initial fetch when component mounts

  // Update the fetchActivities function
  const fetchActivities = async () => {
    try {
      setLoading(true);
      let params = {};

      if (filters.category) {
        params.category = filters.category;
      }

      // Use filter dates if available, otherwise use visible range
      const start = filters.selectedDay || visibleRange.start;
      const end = filters.selectedDay || visibleRange.end;

      // For calendar view: Extend the fetch range to 30 days before and 90 days after
      const extendedStart = new Date(start);
      const extendedEnd = new Date(end);
      extendedStart.setDate(extendedStart.getDate() - 30);
      extendedEnd.setDate(extendedEnd.getDate() + 90);

      // Set time boundaries
      extendedStart.setHours(0, 0, 0, 0);
      extendedEnd.setHours(23, 59, 59, 999);
      
      params.startDate = extendedStart.toISOString();
      params.endDate = extendedEnd.toISOString();
      params.limit = 5000; // Set limit to 5000 activities

      const response = await axios.get('/api/activities', { params });
      
      if (Array.isArray(response.data)) {
        // Update activities state
        setActivities(response.data);
        
        // Update visible activities immediately
        const filtered = response.data.filter(activity => {
          const activityDate = new Date(activity.date);
          const isInVisibleRange = activityDate >= visibleRange.start && activityDate <= visibleRange.end;
          const matchesCategory = !filters.category || activity.category === filters.category;
          const matchesDay = !filters.selectedDay || format(activityDate, 'yyyy-MM-dd') === filters.selectedDay;
          const matchesStatus = !filters.status || 
            (filters.status === 'upcoming' && activityDate >= new Date()) ||
            (filters.status === 'past' && activityDate < new Date()) ||
            filters.status === 'all';
          return isInVisibleRange && matchesCategory && matchesDay && matchesStatus;
        });
        
        setVisibleActivities(filtered);
      } else {
        setError(t('errorFetchingActivities'));
      }
      
      setError(null);
    } catch (err) {
      setError(t('errorFetchingActivities'));
    } finally {
      setLoading(false);
    }
  };

  // Update the handleViewChange function
  const handleViewChange = (newView) => {
    setCalendarView(newView);
    
    // Update visible range based on new view
    let start, end;
    
    switch (newView) {
      case 'month':
        start = startOfMonth(currentDate);
        end = endOfMonth(currentDate);
        break;
      case 'week':
        start = startOfWeek(currentDate, { weekStartsOn: 1 });
        end = endOfWeek(currentDate, { weekStartsOn: 1 });
        break;
      default: // day view
        start = startOfDay(currentDate);
        end = endOfDay(currentDate);
    }
    
    setVisibleRange({ start, end });
    
    // Reset day filter when changing views
    setFilters(prev => ({
      ...prev,
      selectedDay: ''
    }));
    
    // Fetch activities for the new date range
    fetchActivities();
  };

  const handleOpenDialog = (activity = null) => {
    if (activity) {
      setSelectedActivity(activity);
      setFormData({
        title: activity.title,
        shortDescription: activity.shortDescription,
        description: activity.description,
        date: new Date(activity.date),
        duration: activity.duration,
        maxParticipants: activity.maxParticipants,
        location: activity.location || defaultLocation,
        category: activity.category,
        image: null,
        imageUrl: activity.images?.[0]?.url || null,
        featured: activity.featured || false,
        repeatWeekly: activity.repeatWeekly || true,
      });
      setImagePreview(activity.images?.[0]?.url || null);
    } else {
      setSelectedActivity(null);
      setFormData({
        title: '',
        shortDescription: '',
        description: '',
        date: null,
        duration: '60',
        maxParticipants: '10',
        location: defaultLocation,
        category: '',
        image: null,
        imageUrl: null,
        featured: false,
        repeatWeekly: true,
      });
      setImagePreview(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedActivity(null);
    setFormData({
      title: '',
      shortDescription: '',
      description: '',
      date: null,
      duration: '60',
      maxParticipants: '10',
      location: defaultLocation,
      category: '',
      image: null,
      imageUrl: null,
      featured: false,
      repeatWeekly: true,
    });
    setImagePreview(null);
  };

  const getCategoryContent = (category) => {
    const fromApi = typesByName[category];
    if (fromApi && (fromApi.shortDescription || fromApi.longDescription)) {
      return {
        shortDescription: fromApi.shortDescription || '',
        description: fromApi.longDescription || fromApi.shortDescription || ''
      };
    }
    switch(category) {
      case 'Hatha Yoga':
        return {
          shortDescription: "Le hatha yoga est une pratique douce qui associe postures, respiration et relaxation pour renforcer le corps, assouplir et apaiser le mental.",
          description: "Le hatha yoga est une discipline traditionnelle qui combine des postures (asanas), le travail du souffle (pranayama) et des temps de relaxation. Pratiqué à un rythme plutôt lent, il invite à prendre conscience du corps, à améliorer la souplesse, la force et l’équilibre, tout en réduisant le stress et les tensions. Accessible à tous, le hatha yoga favorise un meilleur ancrage, une respiration plus ample et un état de calme durable, séance après séance."
        };
      case 'Yoga Ashtanga':
        return {
          shortDescription: "Le yoga Ashtanga est un yoga très rigoureux et dynamique. Il se compose d'une série de posture qui se pratique toujours dans le même ordre, c'est pourquoi le yoga Ashtanga peut être bénéfique pour les débutants du yoga car il permet d'acquérir les positions et la série avant de se lancer, contrairement au Vinyasa yoga ou l'on ne connaît pas l'enchaînement.",
          description: "Le yoga Ashtanga est un yoga très rigoureux et dynamique. Il se compose d'une série de posture qui se pratique toujours dans le même ordre, c'est pourquoi le yoga Ashtanga peut être bénéfique pour les débutants du yoga car il permet d'acquérir les positions et la série avant de se lancer, contrairement au Vinyasa yoga ou l'on ne connaît pas l'enchaînement.\n\nMon yoga Ashtanga suit la logique de la série première, ce cours est le cours le plus traditionnel que je propose. Je suis l'ordre et l'avancement de la série première décrite par Shrî K.Pattabhi Jois.\nChaque semaine nous revenons sur ce qu'on a appris la semaine d'avant et nous avançons doucement mais sûrement. \nDans ce cours il sera important de bien acquérir les salutations au soleil A et B car leurs enchaînements resteront présents tout au long de la série des postures. Puis nous viendrons décortiquer chacune des positions afin d'obtenir le parfait alignement et de pouvoir bénéficier des bienfaits de la posture. \n\nLes bienfaits du yoga ashtanga\n* Il renforce les articulations du corps par l'utilisation de différentes postures, ces postures sont aussi dynamiques ce qui favorise un bon fonctionnement des articulations.\n* Il aide à réduire les toxines de l'organisme. En effet, le Ashtanga yoga est un yoga dynamique qui permet d'augmenter notre température interne et entraîne une hausse de la transpiration. \n* Par son dynamisme, il augmente l'endurance et la flexibilité.\n* Grâce à la méditation et les exercices de respiration, il aide à réduire le stress et l'anxiété."
        };
      case 'Yoga Vinyasa':
        return {
          shortDescription: "Lors de mon cours de Vinyasa c'est également ma personnalité qui s'exprime à travers des thèmes que je propose chaque semaine.\nNous verrons des thèmes émotionnels (lâcher prise, no stress etc) et des thèmes physiques (yoga pour les genoux, ouverture des hanches etc) mais j'aime aussi suivre les saisons (début d'été, fin d'hiver etc).  Il est complémentaire au cours d'Ashtanga qui est plus rigoureux et qui suit toujours le même schéma. ",
          description: "Lors de mon cours de Vinyasa c'est également ma personnalité qui s'exprime à travers des thèmes que je propose chaque semaine.\nNous verrons des thèmes émotionnels (lâcher prise, no stress etc) et des thèmes physiques (yoga pour les genoux, ouverture des hanches etc) mais j'aime aussi suivre les saisons (début d'été, fin d'hiver etc). \nIl est complémentaire au cours d'Ashtanga qui est plus rigoureux et qui suit toujours le même schéma. \n\nLes bienfaits du yoga Vinyasa: \n* Il aide au renforcement musculaire, à gagner en souplesse et en équilibre.\n* C'est une activité sans impacts donc très douce pour les articulations qui sont fragiles.\n* Il aide à améliorer la posture du corps, en particulier le dos.\n* Il permet d'éviter certaines tensions et/ou douleurs musculaires et articulaires, en particulier sur le dos.\n* Il est un booster d'énergie, il aide à se défouler et retrouver une légèreté dans le corps et l'esprit. "
        };
      case 'Yoga Restauratif':
        return {
          shortDescription: "Le cours de yoga restauratif est un cours de yoga basé sur des postures de relaxation accompagné de pranayama (technique de respiration)",
          description: "Le cours de yoga restauratif est un cours de yoga basé sur des postures de relaxation accompagné de pranayama (technique de respiration). \nNous viendrons nous détendre en fin de semaine et essayer de lâcher prise dans des postures d'étirements et de détente. Accompagné ou pas du bolster, le cours de yoga restauratif vous invite à vous détendre et à décompresser tout en douceur.. »"
        };
      case 'Yin Yoga':
        return {
          shortDescription: "Le yin yoga est grandement influencé par les philosophies asiatiques, le Taoïsme et la médecine traditionnelle chinoise. Cela étant, la médecine conventionnelle comme nous la connaissons en France reconnaît l'importance cruciale d'activités telles que le yin, qui lient physique et psychique.",
          description: "Le yin yoga est grandement influencé par les philosophies asiatiques, le Taoïsme et la médecine traditionnelle chinoise. Cela étant, la médecine conventionnelle comme nous la connaissons en France reconnaît l'importance cruciale d'activités telles que le yin, qui lient physique et psychique. Vous le savez peut-être, la médecine chinoise fait appel aux points d'acupression, aux plantes, etc… Alors, pas de telles pratiques prévues sur votre tapis, mais les explications feront souvent référence à la médecine chinoise.\nEn yin, on tentera de prendre conscience des méridiens du corps. Chaque méridien est associé à différents organes ou différentes zones du corps pour mieux y faire circuler l'énergie, le chi. Celui-ci circule dans notre corps à travers les méridiens qui, s'ils sont bien irrigués, sont garants de notre équilibre physique et moral.\nLe yin permet de travailler sur les tissus conjonctifs, passifs – les fascias. Ceux-ci sont des tissus plutôt fibreux, qui contiennent du collagène. Il existe différents fascias, mais nous n'allons pas rentrer dans le détail aujourd'hui. En yin, on exerce une sorte de pression sur les fascias pour les garder hydratés : avec son poids  et la gravité, votre corps va petit à petit se fondre dans le sol et rendre l'action sur les fascias très efficace.  Par ailleurs, le yin stimule aussi le système nerveux parasympathique, celui responsable de la régulation de notre fréquence cardiaque et de notre digestion. Très complet, en somme !\nLa stimulation musculaire est donc à son minimum et le yin amène davantage à un état méditatif. La voix de l'enseignant.e saura vous accompagner à bon escient vers l'acceptation et le lâcher prise nécessaires pour obtenir tous les bienfaits du yin. Vous verrez aussi comme le souffle guide la pratique et comme il change selon votre état d'esprit !\nLes postures se pratiquent au sol et elles sont tenues pendant plusieurs minutes. On encouragera l'utilisation d'accessoires : briques, coussins de type bolsters, zafus, sangles etc. Ces éléments soutiennent le corps pendant la pratique et aident au relâchement.\nLe yin n'est pas toujours confortable et nécessite d'accepter de sortir de sa zone de confort et relève réellement d'une pratique méditative : le yin nous montre à quel point corps et esprit sont liés. En yin, il faut savoir faire la différence entre inconfort et véritables douleurs.\nBonne pratique à vous <3"
        };
      case 'Shiatsu':
        return {
          shortDescription: "Le shiatsu est une thérapie manuelle d'origine japonaise qui utilise des pressions, étirements et mobilisations pour équilibrer l'énergie vitale du corps. Il se pratique par dessus les vêtements. Inspirée de la Médecine Traditionnelle Chinoise, cette pratique douce favorise la relaxation, soulage les tensions et renforce le bien-être global.",
          description: "Le shiatsu est une thérapie manuelle d'origine japonaise qui utilise des pressions, étirements et mobilisations pour équilibrer l'énergie vitale du corps. Il se pratique par dessus les vêtements. Inspirée de la Médecine Traditionnelle Chinoise, cette pratique douce favorise la relaxation, soulage les tensions et renforce le bien-être global. Le shiatsu, qui signifie \"pression des doigts\" en japonais, est une technique holistique basée sur les principes de la Médecine Traditionnelle Chinoise. Cette pratique repose sur l'application de pressions ciblées, réalisées avec les doigts, les paumes et parfois les coudes, le long des méridiens énergétiques du corps.\n\nL'objectif du shiatsu est de rétablir la circulation harmonieuse de l'énergie vitale (le Qi), souvent perturbée par le stress, la fatigue, ou des déséquilibres internes. Chaque séance est adaptée aux besoins spécifiques du receveur, qu'il s'agisse de soulager des douleurs physiques, de réduire le stress ou d'améliorer l'équilibre émotionnel.\n\nBienfaits principaux du shiatsu :\n\t•\tRelâche les tensions musculaires et soulage les douleurs.\n\t•\tRéduit le stress et améliore la qualité du sommeil.\n\t•\tStimule le système immunitaire et favorise l'équilibre énergétique.\n\t•\tAccompagne les troubles chroniques et émotionnels en douceur.\n\nAccessible à tous, le shiatsu peut être pratiqué en prévention ou en complément d'autres approches médicales. Sur futon ou assis, chaque séance est un moment unique de connexion entre le corps et l'esprit.\n\nPrenez soin de vous avec cette pratique ancestrale qui allie tradition et modernité pour un bien-être profond et durable."
        };
      case 'Chi Nei Tsang':
        return {
          shortDescription: "Le Chi Nei Tsang est une technique de massage abdominal issue de la tradition taoïste. En agissant sur le ventre, considéré comme le centre énergétique du corps, il aide à libérer les tensions, améliorer la digestion et équilibrer les émotions pour un bien-être profond.",
          description: "Le Chi Nei Tsang, qui signifie \"transformation de l'énergie des organes internes\", est une méthode de massage abdominal développée par les moines taoïstes. Cette pratique repose sur l'idée que le ventre est non seulement le centre physique, mais aussi émotionnel et énergétique du corps.\n\nEn appliquant des pressions douces et ciblées sur l'abdomen, le Chi Nei Tsang permet de :\n\t•\tLibérer les tensions profondes accumulées dans les organes internes.\n\t•\tStimuler la circulation énergétique pour harmoniser le corps et l'esprit.\n\t•\tAméliorer les fonctions digestives et détoxifier l'organisme.\n\t•\tApaiser les émotions bloquées comme le stress, la colère ou l'anxiété.\n\nBienfaits principaux du Chi Nei Tsang :\n\t•\tSoulage les troubles digestifs (ballonnements, constipation).\n\t•\tRenforce le système immunitaire en stimulant les organes internes.\n\t•\tFavorise une meilleure gestion des émotions.\n\t•\tApporte une relaxation profonde et un recentrage énergétique.\n\nAccessible à tous, le Chi Nei Tsang est particulièrement adapté aux personnes ressentant des inconforts abdominaux, des déséquilibres émotionnels ou souhaitant se reconnecter à leur énergie intérieure.\n\nOffrez à votre ventre – et à vous-même – un soin bienveillant et libérateur avec cette pratique ancestrale qui agit en profondeur. "
        };
      case 'Soin':
        return {
          shortDescription: "Il y a trois sortes de soins disponible que je propose à la yoga yourte du Luberon: le shiatsu, le massage Chi Nei Tsang, et le massage intuitif.",
          description: "Il y a trois sortes de soins disponible que je propose à la yoga yourte du Luberon: \n\n•⁠  ⁠Le  shiatsu - 60min \n•⁠  ⁠Le massage Chi Nei Tsang - 90min\n•⁠  ⁠Le massage intuitif - 60 ou 90min \n\nVous pouvez retrouver le descriptif complet de ces trois soins, ainsi que leur tarif, à l'onglet accueil qui présente toutes les activités. \n\nLorsque vous réservez merci de m'envoyer un message au 0618254709 afin de me préciser quel soin vous avez choisis. \nVeillez, s'il vous plait, a bien lire le mail de confirmation que vous allez recevoir pour venir dans la tenue adéquate et bien vous préparer pour la réception du soin. \n\nEn vous remerciant, \nAmélie"
        };
      case 'Pilates Doux':
        return {
          shortDescription: "Le Pilates doux est une version accessible et bienveillante de la méthode Pilates, pensée pour celles et ceux qui souhaitent (re)découvrir leur corps en douceur, à leur rythme.",
          description: "Ce cours met l'accent sur les fondamentaux du mouvement : la respiration, la posture, l'alignement et la conscience corporelle. On y travaille les muscles profonds — en particulier ceux du centre (abdos, dos, plancher pelvien) — dans des enchaînements simples, fluides et sans impact.\n\n👵🏻🧓 Adapté aux seniors, débutants ou corps sensibles\nCe cours est idéal si vous :\n•⁠  ⁠êtes senior ou en reprise d'activité après une pause\n•⁠  ⁠découvrez le Pilates pour la première fois\n•⁠  ⁠souffrez de douleurs chroniques (dos, hanches, épaules…)\n•⁠  ⁠cherchez une pratique préventive, stable et sécurisante\nChaque séance est progressive, avec des options adaptées aux besoins de chacun, et des accessoires (briques, ballons, coussins) pour plus de confort.\n\n✨ Les bienfaits du Pilates doux\n✔️ Améliore la posture et la mobilité articulaire\n✔️ Renforce en douceur les muscles profonds\n✔️ Soulage les tensions et les douleurs\n✔️ Favorise l'équilibre et la coordination\n✔️ Apporte calme, confiance et présence à soi\n\nUne pratique douce, pour aller mieux sans forcer.\nReconnecter le corps et l'esprit, un mouvement à la fois."
        };
      case 'Power Pilates':
        return {
          shortDescription: "Le Power Pilates est une version tonique et dynamique du Pilates traditionnel. Il s'adresse à celles et ceux qui souhaitent aller plus loin dans le renforcement musculaire, le gainage et la maîtrise du mouvement.",
          description: "Inspiré des principes du Pilates classique (respiration, centrage, précision), ce cours ajoute une intensité renforcée et un rythme plus soutenu, tout en restant respectueux du corps et de ses alignements.\n\n💪 Pour qui ?\nCe cours est idéal si vous :\n•⁠  ⁠avez déjà pratiqué le Pilates et souhaitez intensifier votre pratique\n•⁠  ⁠recherchez un entraînement efficace sans sauts ni impacts\n•⁠  ⁠voulez sculpter votre silhouette et renforcer votre centre (core)\n•⁠  ⁠aimez bouger de façon fluide, précise et engagée\n\n⚡️ Les bienfaits du Power Pilates\n✔️ Renforce en profondeur abdominaux, dos, bras et jambes\n✔️ Améliore la posture et la stabilité globale\n✔️ Tonifie et allonge les muscles sans les \"charger\"\n✔️ Augmente la résistance musculaire et la coordination\n✔️ Stimule l'énergie et la concentration\n\nUn entraînement complet, intelligent et exigeant, dans le respect du mouvement et de la respiration.\nBougez avec force. Restez aligné.e. Ressentez la différence."
        };
      case 'Pilates - Tous niveaux':
        return {
          shortDescription: "Une pratique où la force naît de la douceur : chaque mouvement devient une exploration du corps, du souffle et de la présence.",
          description: "Un moment pour revenir au corps, respirer, bouger avec conscience et équilibre.\nCe cours invite à renforcer en profondeur tout en cultivant la fluidité, la précision et la présence à soi.\n\nOn y explore les fondamentaux du mouvement — respiration, posture, alignement et conscience corporelle — à travers des enchaînements sans impact, accessibles et progressifs. Le travail du centre (abdos, dos, plancher pelvien) s'accompagne d'options plus douces ou plus toniques selon les besoins de chacun·e.\n\n💫 Pour qui ?\nIdéal si vous souhaitez :\n•⁠  ⁠découvrir le Pilates ou reprendre une activité en douceur\n•⁠  ⁠renforcer et tonifier votre corps en profondeur\n•⁠  ⁠améliorer votre posture et votre mobilité\n•⁠  ⁠bouger avec conscience, fluidité et stabilité\n\n✨ Les bienfaits\n✔️ Renforce les muscles profonds et stabilisateurs\n✔️ Améliore la posture, la souplesse et la coordination\n✔️ Soulage les tensions et favorise l'équilibre\n✔️ Stimule l'énergie, la concentration et la confiance en soi\n\nUne pratique intelligente et progressive, qui relie force et douceur, mouvement et présence."
        };
      case 'Yoga des Énergies':
        return {
          shortDescription: "Le Yoga des Énergies est une forme de yoga douce et introspective, développée principalement en France, qui met l’accent sur la circulation de l’énergie vitale (le prāṇa) dans le corps à travers la respiration, la concentration, la posture et l’attention intérieure.",
          description: "Le Yoga des Énergies se base sur la conviction que nous pouvons sentir, guider et harmoniser notre énergie par des gestes simples, une respiration consciente et une qualité de présence à soi.\n\n🧘‍♀️ Caractéristiques du Yoga des Énergies :\n•⁠ Postures simples, tenues dans l'écoute et la détente\n•⁠ Travail très fin sur le souffle (prāṇāyāma) et le rythme respiratoire\n•⁠ Importance de l'ancrage, de la verticalité et de l'attention intérieure\n•⁠ Visualisations, gestes conscients (mudrās), et mouvements lents pour accompagner l'énergie\n•⁠ Pratique souvent statique, méditative et très intériorisée\nC'est un yoga non sportif, mais profondément transformateur, qui agit à un niveau plus subtil que le simple renforcement physique.\n\n🌟 Bienfaits du Yoga des Énergies :\n✔️ Apaisement du mental et du système nerveux\n✔️ Meilleure circulation de l'énergie vitale\n✔️ Développement de l'écoute de soi, de la présence et de la concentration\n✔️ Amélioration de la posture, de la respiration et de la détente profonde\n✔️ Harmonisation corps – souffle – esprit"
        };
      default:
        return {
          shortDescription: '',
          description: ''
        };
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('location.')) {
      const locationField = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          [locationField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(name === 'category' ? {
          title: (typesByName[value]?.defaultTitle || typesByName[value]?.displayName || value),
          duration: getDuration(value),
          maxParticipants: getDefaultMaxParticipants(value),
          ...getCategoryContent(value)
        } : {}),
      }));
    }
  };

  const handleDateChange = (newDate) => {
    if (!newDate) return;
    
    setFormData((prev) => {
      const currentDate = prev.date || new Date();
      const updatedDate = new Date(newDate);
      updatedDate.setHours(currentDate.getHours());
      updatedDate.setMinutes(currentDate.getMinutes());
      
      // Ensure time is between 8h and 22h
      const hours = updatedDate.getHours();
      const minutes = updatedDate.getMinutes();
      
      if (hours < 7) {
        updatedDate.setHours(7, 0, 0);
      } else if (hours >= 22) {
        updatedDate.setHours(21, 59, 0);
      }
      
      return {
        ...prev,
        date: updatedDate,
      };
    });
  };

  const handleTimeChange = (e) => {
    try {
      if (!e.target.value) return;
      
      const [hours, minutes] = e.target.value.split(':').map(Number);
      const newTime = new Date(formData.date || new Date());
      
      // Only proceed if we have valid numbers
      if (!isNaN(hours) && !isNaN(minutes)) {
              // Ensure time is between 7h and 22h
      const validHours = Math.min(Math.max(hours, 7), 21);
        const validMinutes = hours >= 22 ? 59 : (minutes || 0);
        
        newTime.setHours(validHours);
        newTime.setMinutes(validMinutes);
        
        setFormData(prev => ({
          ...prev,
          date: newTime
        }));
      }
    } catch (error) {
      // Silently handle time input errors
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setError(t('errorInvalidImageType'));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageSelect = (image) => {
    const relativeUrl = image.url || image.path || '';
    const previewUrl = getImageUrl(relativeUrl);

    setFormData((prev) => ({
      ...prev,
      imageUrl: relativeUrl,
      image: null,
    }));

    setImagePreview(previewUrl);
    setOpenImageDialog(false);
  };

  const getRepeatedDates = (startDate) => {
    if (!startDate || !formData.repeatWeekly) return [startDate];
    
    const dates = [];
    const currentDate = new Date(startDate);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59);

    // Ensure we're working with the correct time
    const hours = currentDate.getHours();
    const minutes = currentDate.getMinutes();
    
    while (currentDate <= endOfMonth) {
      const newDate = new Date(currentDate);
      // Ensure each date has the correct time
      newDate.setHours(hours);
      newDate.setMinutes(minutes);
      dates.push(newDate);
      currentDate.setDate(currentDate.getDate() + 7);
    }
    
    return dates;
  };

  const generateUniqueSlug = (title, date) => {
    const formattedDate = format(new Date(date), 'yyyy-MM-dd-HHmm');
    const timeStamp = format(new Date(), 'HHmmss');
    const randomString = Math.random().toString(36).substring(2, 8);
    return `${title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')}-${formattedDate}-${timeStamp}-${randomString}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      if (!formData.title || !formData.category || !formData.date || !formData.duration || !formData.maxParticipants) {
        setError('Please fill in all required fields');
        return;
      }

      // If editing an existing activity
      if (selectedActivity || activityManagementDialog.activity) {
        const activity = selectedActivity || activityManagementDialog.activity;
        const oldDate = new Date(activity.date);
        const slug = generateUniqueSlug(formData.title, formData.date);

        const updateData = new FormData();
        updateData.append('title', formData.title);
        updateData.append('shortDescription', formData.shortDescription || '');
        updateData.append('description', formData.description || '');
        updateData.append('date', formData.date.toISOString());
        updateData.append('duration', parseInt(formData.duration));
        updateData.append('maxParticipants', parseInt(formData.maxParticipants));
        updateData.append('location', JSON.stringify(formData.location));
        updateData.append('category', formData.category);
        updateData.append('featured', formData.featured);
        updateData.append('slug', slug);

        if (formData.image) {
          updateData.append('image', formData.image);
        } else if (formData.imageUrl) {
          updateData.append('imageUrl', formData.imageUrl);
        }

        const response = await axios.put(`/api/activities/${activity._id}`, updateData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Check if date or maxParticipants changed
        const dateChanged = oldDate.getTime() !== formData.date.getTime();
        const capacityChanged = activity.maxParticipants !== parseInt(formData.maxParticipants);
        
        if (dateChanged || capacityChanged) {
          // Notify participants of the change
          await notifyParticipantsOfUpdate(response.data, 'update', oldDate);
          
          // If capacity decreased, handle subscription updates
          if (capacityChanged && parseInt(formData.maxParticipants) < activity.maxParticipants) {
            await updateSubscriptions(response.data, 'refund');
          }
        }

        if (activityManagementDialog.open) {
          handleCloseActivityManagement();
        } else {
          handleCloseDialog();
        }
      } else {
        // Creating new activities
        const dates = getRepeatedDates(formData.date);
        let firstActivityImageUrl = null;
        
        // Create first activity
        const firstDate = dates[0];
        const firstFormattedDate = format(firstDate, 'yyyy-MM-dd-HHmm');
        const timeStamp = format(new Date(), 'HHmmss');
        const firstSlug = `${formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}-${firstFormattedDate}-${timeStamp}`;

        let firstResponse;
        if (formData.image || formData.imageUrl) {
          // If we have an image, use FormData
          const firstActivityData = new FormData();
          firstActivityData.append('title', formData.title);
          firstActivityData.append('shortDescription', formData.shortDescription || '');
          firstActivityData.append('description', formData.description || '');
          firstActivityData.append('date', firstDate.toISOString());
          firstActivityData.append('duration', parseInt(formData.duration));
          firstActivityData.append('maxParticipants', parseInt(formData.maxParticipants));
          firstActivityData.append('location', JSON.stringify(formData.location));
          firstActivityData.append('category', formData.category);
          firstActivityData.append('featured', formData.featured);
          firstActivityData.append('repeatWeekly', formData.repeatWeekly);
          firstActivityData.append('slug', firstSlug);

          if (formData.image) {
            firstActivityData.append('image', formData.image);
          } else if (formData.imageUrl) {
            firstActivityData.append('imageUrl', formData.imageUrl);
          }

          firstResponse = await axios.post('/api/activities', firstActivityData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          // If no image, use JSON
          const firstActivityData = {
            title: formData.title,
            shortDescription: formData.shortDescription || '',
            description: formData.description || '',
            date: firstDate.toISOString(),
            duration: parseInt(formData.duration),
            maxParticipants: parseInt(formData.maxParticipants),
            location: formData.location,
            category: formData.category,
            featured: formData.featured,
            repeatWeekly: formData.repeatWeekly,
            slug: firstSlug
          };

          firstResponse = await axios.post('/api/activities', firstActivityData);
        }

        firstActivityImageUrl = firstResponse.data.images?.[0]?.url;

        // If we have recurring activities, create them with the image URL from the first activity
        if (dates.length > 1) {
          for (let i = 1; i < dates.length; i++) {
            const date = dates[i];
            const formattedDate = format(date, 'yyyy-MM-dd-HHmm');
            const slug = `${formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}-${formattedDate}`;

            const activityData = {
              title: formData.title,
              shortDescription: formData.shortDescription || '',
              description: formData.description || '',
              date: date.toISOString(),
              duration: parseInt(formData.duration),
              maxParticipants: parseInt(formData.maxParticipants),
              location: formData.location,
              category: formData.category,
              featured: formData.featured,
              repeatWeekly: formData.repeatWeekly,
              slug: slug
            };

            if (firstActivityImageUrl) {
              activityData.imageUrl = firstActivityImageUrl;
            }

            await axios.post('/api/activities', activityData);

            // Add delay between requests
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        handleCloseDialog();
      }
      
      fetchActivities();
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    }
  };

  const handleActivityDelete = async (activity) => {
    if (!activity) return;
    
    const isRecurring = activity.repeatWeekly;
    
    if (isRecurring) {
      const confirmResult = window.confirm(
        'Voulez-vous supprimer toutes les occurrences de cette activité pour le reste du mois ?\n\n' +
        'OK = Supprimer toutes les occurrences\n' +
        'Annuler = Supprimer uniquement cette occurrence'
      );

      try {
        if (confirmResult) {
          // Delete all recurring instances
          const activityDate = new Date(activity.date);
          const endOfMonth = new Date(activityDate.getFullYear(), activityDate.getMonth() + 1, 0);
          
          await axios.delete(`/api/activities/${activity._id}`, {
            params: {
              deleteRecurring: true,
              category: activity.category,
              startDate: activityDate.toISOString(),
              endDate: endOfMonth.toISOString()
            }
          });
        } else {
          // Delete single instance
          await axios.delete(`/api/activities/${activity._id}`);
        }

        // Close the activity management dialog if it's open
        if (activityManagementDialog.open) {
          handleCloseActivityManagement();
        }

        // Refresh activities
        fetchActivities();
      } catch (err) {
        setError(t('errorDeleting'));
      }
    } else {
      // Non-recurring activity - simple delete
      if (window.confirm(t('confirmDelete'))) {
        try {
          await axios.delete(`/api/activities/${activity._id}`);
          
          // Close the activity management dialog if it's open
          if (activityManagementDialog.open) {
            handleCloseActivityManagement();
          }

          // Refresh activities
          fetchActivities();
        } catch (err) {
          setError(t('errorDeleting'));
        }
      }
    }
  };

  const handleDelete = (activity) => {
    handleActivityDelete(activity);
  };

  const categories = typesReady && typeNames.length
    ? typeNames
    : [
    'Yoga Ashtanga',
    'Hatha Yoga',
    'Yoga Vinyasa',
    'Yoga Restauratif',
    'Yin Yoga',
    'Yoga des Énergies',
    'Shiatsu',
    'Chi Nei Tsang',
    'Soin',
    'Ateliers',
    'Pilates Doux',
    'Power Pilates',
    'Pilates - Tous niveaux',
    'Autres'
  ];

  const handleSlotSelect = (slotInfo) => {
    const selectedDate = new Date(slotInfo.start);
    // Keep the exact time from the slot selection
    const hours = selectedDate.getHours();
    const minutes = selectedDate.getMinutes();
    
    // Ensure time is within working hours (7:00-22:00)
    const validHours = Math.min(Math.max(hours, 7), 21);
    const validMinutes = hours >= 22 ? 59 : minutes;
    
    selectedDate.setHours(validHours);
    selectedDate.setMinutes(validMinutes);
    
    setFormData(prev => ({
      ...prev,
      date: selectedDate,
      title: '',
      shortDescription: '',
      description: '',
      duration: '60',
      maxParticipants: '10',
      location: defaultLocation,
      category: '',
      image: null,
      imageUrl: null,
      featured: false,
      repeatWeekly: true,
    }));
    setSelectedActivity(null);
    setOpenDialog(true);
  };

  // Calendar event style
  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: categoryColors[event.category] || '#808080',
      color: '#fff',
      border: 'none',
      borderRadius: '3px',
      whiteSpace: 'pre-line',
      padding: '4px',
      fontSize: '0.8rem',
      lineHeight: 1.3,
      maxHeight: '5.2em',
      overflow: 'hidden'
    }
  });

  // Add dayPropGetter for styling public holidays
  const dayPropGetter = (date) => {
    if (isFrenchPublicHoliday(date)) {
      return {
        style: {
          backgroundColor: '#2E7D32',
          color: 'white',
        },
      };
    }
    return {};
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResetFilters = () => {
    // First reset filters
    setFilters({
      category: '',
      selectedDay: '',
      status: 'upcoming'
    });

    // Reset to default view based on screen size
    const defaultView = window.innerWidth <= 768 ? 'day' : 'week';
    setCalendarView(defaultView);

    // Use handleNavigate which properly updates the calendar
    const today = new Date();
    handleNavigate(today);

    // Force a re-render of the calendar by slightly modifying and then restoring the date
    setTimeout(() => {
      const tempDate = new Date(today);
      tempDate.setMinutes(tempDate.getMinutes() + 1);
      handleNavigate(tempDate);
      setTimeout(() => {
        handleNavigate(today);
      }, 0);
    }, 0);
  };

  /**
   * Get available days from activities
   * @param {Array} activities - Array of activities
   * @returns {Array<{value: string, label: string}>}
   */
  const getAvailableDays = (activities) => {
    // Filter activities based on visible range
    const filteredActivities = activities.filter(activity => {
      const activityDate = new Date(activity.date);
      return activityDate >= visibleRange.start && activityDate <= visibleRange.end;
    });

    const days = filteredActivities.map(activity => ({
      value: format(new Date(activity.date), 'yyyy-MM-dd'),
      label: format(new Date(activity.date), 'EEEE d MMMM yyyy', { locale: fr })
    }));
    
    return Array.from(new Set(days.map(JSON.stringify)))
      .map(JSON.parse)
      .sort((a, b) => new Date(a.value).getTime() - new Date(b.value).getTime());
  };

  const handleCloseParticipantsDialog = () => {
    setParticipantsDialog({
      open: false,
      activity: null,
      participants: []
    });
  };

  const fetchParticipants = async (activity) => {
    try {
      const response = await axios.get(`/api/activities/${activity._id}/participants`);
      setParticipantsDialog({
        open: true,
        activity: activity,
        participants: response.data
      });
    } catch (error) {
      console.error('Error fetching participants:', error);
      setError(t('errorFetchingParticipants'));
    }
  };

  const handleParticipantUpdate = async () => {
    if (participantsDialog.activity) {
      await fetchParticipants(participantsDialog.activity);
      await fetchActivities(); // Refresh activities data
    }
  };

  // Add date validation helper
  const isValidDate = (date) => {
    return date && !isNaN(new Date(date).getTime());
  };

  // Add handleSelectEvent function
  const handleSelectEvent = async (event) => {
    try {
      const response = await axios.get(`/api/activities/${event.activity._id}/participants`);
      setActivityManagementDialog({
        open: true,
        activity: event.activity,
        participants: response.data,
        view: 'participants'
      });
    } catch (error) {
      console.error('Error fetching participants:', error);
      setError(t('errorFetchingParticipants'));
    }
  };

  const formatRegistrationDate = (date) => {
    if (!date) return t('noRegistrationDate');
    try {
      const parsedDate = new Date(date);
      return !isNaN(parsedDate.getTime()) 
        ? format(parsedDate, 'dd/MM/yyyy', { locale: fr })
        : t('noRegistrationDate');
    } catch {
      return t('noRegistrationDate');
    }
  };

  // Add resize handler for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setCalendarView(window.innerWidth <= 768 ? 'day' : 'week');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update initial calendar view based on screen size
  useEffect(() => {
    setCalendarView(window.innerWidth <= 768 ? 'day' : 'week');
  }, []);

  // Add new state for activity management dialog
  const [activityManagementDialog, setActivityManagementDialog] = useState({
    open: false,
    activity: null,
    participants: [],
    view: 'participants' // 'participants' or 'edit'
  });

  // Add function to handle activity update notification
  const notifyParticipantsOfUpdate = async (activity, updateType, oldDate) => {
    const isIndividualSession = ['Soin', 'Shiatsu', 'Chi Nei Tsang'].includes(activity.category);
    if (isIndividualSession) {
      return; // Don't send notifications for individual sessions
    }

    try {
      await axios.post(`/api/activities/${activity._id}/notify`, {
        type: updateType,
        oldDate: oldDate ? oldDate.toISOString() : null
      });
    } catch (error) {
      console.error('Error sending notifications:', error);
      setError(t('errorSendingNotifications'));
    }
  };

  // Add function to handle subscription updates
  const updateSubscriptions = async (activity, action) => {
    const isIndividualSession = ['Soin', 'Shiatsu', 'Chi Nei Tsang'].includes(activity.category);
    if (isIndividualSession) {
      return; // Don't update subscriptions for individual sessions
    }

    try {
      await axios.post(`/api/activities/${activity._id}/update-subscriptions`, {
        action: action // 'refund' or 'deduct'
      });
    } catch (error) {
      console.error('Error updating subscriptions:', error);
      setError(t('errorUpdatingSubscriptions'));
    }
  };

  // Add dialog close handler
  const handleCloseActivityManagement = () => {
    setActivityManagementDialog({
      open: false,
      activity: null,
      participants: [],
      view: 'participants'
    });
  };

  // Add function to load activity data into form
  const loadActivityDataIntoForm = (activity) => {
    setFormData({
      title: activity.title,
      shortDescription: activity.shortDescription,
      description: activity.description,
      date: new Date(activity.date),
      duration: activity.duration.toString(),
      maxParticipants: activity.maxParticipants.toString(),
      location: activity.location || defaultLocation,
      category: activity.category,
      image: null,
      imageUrl: activity.images?.[0]?.url || null,
      featured: activity.featured || false,
      repeatWeekly: activity.repeatWeekly || false,
    });
    setImagePreview(activity.images?.[0]?.url || null);
  };

  // Update toggleActivityManagementView
  const toggleActivityManagementView = () => {
    setActivityManagementDialog(prev => {
      const newView = prev.view === 'participants' ? 'edit' : 'participants';
      if (newView === 'edit' && prev.activity) {
        loadActivityDataIntoForm(prev.activity);
      }
      return {
        ...prev,
        view: newView
      };
    });
  };

  // Update the activity management dialog content
  return (
    <Box sx={{ p: 3 }}>
      <Container>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" component="h1">
              {t('activityManagement')}
            </Typography>
            {pageTab === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                {t('addActivity')}
              </Button>
            )}
          </Box>

          <Tabs
            value={pageTab}
            onChange={(_, v) => setPageTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={t('activities') || 'Séances'} />
            <Tab label="Images" />
          </Tabs>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {pageTab === 1 && (
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Galerie d&apos;images
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Importez ou supprimez des photos sans créer de séance. La même galerie est partagée
                avec les types d&apos;activités.
              </Typography>
              <ImageGalleryManager
                selectable={false}
                helperText="Importez une photo JPG/PNG, ou supprimez une vignette via la corbeille."
              />
            </Paper>
          )}

          {pageTab === 0 && (loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Calendar
                  localizer={localizer}
                  events={visibleActivities.map((activity) =>
                    buildCalendarEvent(activity, {
                      displayNames: apiDisplayNames,
                      typesByName,
                      t,
                    })
                  )}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ 
                    height: isMobile ? '400px' : '700px',
                    width: '100%',
                    '--time-slot-font-size': isMobile ? '0.7rem' : '0.85rem'
                  }}
                  view={calendarView}
                  onView={handleViewChange}
                  date={currentDate}
                  onNavigate={handleNavigate}
                  eventPropGetter={eventStyleGetter}
                  dayPropGetter={dayPropGetter}
                  onSelectEvent={handleSelectEvent}
                  onSelectSlot={handleSlotSelect}
                  selectable
                  step={30}
                  timeslots={2}
                  min={new Date(new Date().setHours(7, 0, 0))}
                  max={new Date(new Date().setHours(21, 0, 0))}
                  scrollToTime={new Date(new Date().setHours(7, 0, 0))}
                  dayLayoutAlgorithm="no-overlap"
                  formats={{
                    timeGutterFormat: (date, culture, localizer) =>
                      localizer.format(date, 'HH:mm', culture),
                    eventTimeRangeFormat: () => '',
                    dayFormat: (date, culture, localizer) =>
                      `${localizer.format(date, 'EEEE', culture)} ${localizer.format(date, 'd', culture)}`
                  }}
                  messages={{
                    month: t('monthView'),
                    week: t('weekView'),
                    day: t('dayView'),
                    today: t('today'),
                    previous: t('previous'),
                    next: t('next')
                  }}
                  culture="fr"
                  sx={calendarStyles}
                />
              </Paper>
              <Paper elevation={3} sx={{ p: 2, mt: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t('activities')} - {calendarView === 'month' 
                      ? t('monthView')
                      : calendarView === 'week'
                      ? t('weekView')
                      : t('dayView')}
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>{t('category')}</InputLabel>
                        <Select
                          value={filters.category}
                          onChange={(e) => handleFilterChange('category', e.target.value)}
                          label={t('category')}
                        >
                          <MenuItem value="">{t('all')}</MenuItem>
                          {categories.map((category) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>{t('day')}</InputLabel>
                        <Select
                          value={filters.selectedDay}
                          onChange={(e) => handleFilterChange('selectedDay', e.target.value)}
                          label={t('day')}
                        >
                          <MenuItem value="">{t('all')}</MenuItem>
                          {getAvailableDays(activities).map((day) => (
                            <MenuItem key={day.value} value={day.value}>
                              {day.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>{t('status')}</InputLabel>
                        <Select
                          value={filters.status}
                          onChange={(e) => handleFilterChange('status', e.target.value)}
                          label={t('status')}
                        >
                          <MenuItem value="all">{t('all')}</MenuItem>
                          <MenuItem value="upcoming">{t('upcoming')}</MenuItem>
                          <MenuItem value="past">{t('past')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          onClick={handleResetFilters}
                          startIcon={<ClearIcon />}
                        >
                          {t('resetFilters')}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={{ height: 400 }}>
                  <DataGrid
                    rows={activities.filter(activity => {
                      const activityDate = new Date(activity.date);
                      const isInVisibleRange = activityDate >= visibleRange.start && activityDate <= visibleRange.end;
                      const matchesCategory = !filters.category || activity.category === filters.category;
                      const matchesDay = !filters.selectedDay || format(activityDate, 'yyyy-MM-dd') === filters.selectedDay;
                      const now = new Date();
                      const matchesStatus = filters.status === 'all' ||
                        (filters.status === 'upcoming' && activityDate >= now) ||
                        (filters.status === 'past' && activityDate < now);
                      
                      return isInVisibleRange && matchesCategory && matchesDay && matchesStatus;
                    })}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    getRowId={(row) => row._id}
                    disableSelectionOnClick
                    initialState={{
                      sorting: {
                        sortModel: [{ field: 'date', sort: 'asc' }],
                      },
                      pagination: {
                        pageSize: 25,
                      },
                    }}
                    sx={{
                      '& .MuiDataGrid-columnHeader': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600
                      },
                      '& .MuiDataGrid-cell': {
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                  />
                </Box>
              </Paper>
            </>
          ))}
        </Stack>

        {/* Activity Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedActivity ? t('editActivity') : t('addActivity')}
          </DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 2 }}>
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <FormLabel>{t('category')}</FormLabel>
                  <TextField
                    select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    fullWidth
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>

                <TextField
                  name="title"
                  label={t('title')}
                  value={formData.title}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
                
                <TextField
                  name="shortDescription"
                  label={t('shortDescription')}
                  value={formData.shortDescription}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                />

                <TextField
                  name="description"
                  label={t('description')}
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={4}
                  required
                />

                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box sx={{ 
                      width: '100%',
                      '& .MuiPickersLayout-root': {
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }
                    }}>
                      <DatePicker
                        value={formData.date}
                        onChange={handleDateChange}
                        label={t('date')}
                        format="EEEE d MMMM"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            sx: {
                              '& .MuiInputLabel-root': {
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              },
                              '& .MuiInputBase-input': {
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              }
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPickersDay-root': {
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              },
                              '& .MuiTypography-root': {
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              },
                              '& .MuiPickersCalendarHeader-label': {
                                fontSize: { xs: '1rem', sm: '1.1rem' }
                              }
                            }
                          }
                        }}
                      />
                    </Box>
                    <Stack spacing={2} sx={{ width: '100%' }}>
                      <TextField
                        label={t('time')}
                        type="time"
                        value={formData.date ? format(formData.date, 'HH:mm') : '08:00'}
                        onChange={handleTimeChange}
                        inputProps={{
                          step: 900, // 15 minutes in seconds
                          min: '08:00',
                          max: '22:00'
                        }}
                        fullWidth
                        required
                        sx={{
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.9rem', sm: '1rem' }
                          },
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.9rem', sm: '1rem' }
                          }
                        }}
                      />
                    </Stack>
                  </Stack>
                </LocalizationProvider>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <TextField
                    name="duration"
                    label={t('duration')}
                    value={formData.duration}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    type="number"
                    helperText={t('durationHelp')}
                  />
                  {!selectedActivity && (  // Only show repeat switch for new activities
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.repeatWeekly}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            repeatWeekly: e.target.checked
                          }))}
                        />
                      }
                      label={t('repeatWeekly')}
                      sx={{
                        minWidth: { xs: '100%', sm: '200px' },
                        ml: { xs: 0, sm: 2 },
                        '& .MuiFormControlLabel-label': {
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        }
                      }}
                    />
                  )}
                </Stack>
                <TextField
                  name="maxParticipants"
                  label={t('maxParticipants')}
                  value={formData.maxParticipants}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  type="number"
                />
                <FormControl fullWidth>
                  <FormLabel>{t('location')}</FormLabel>
                  <Stack spacing={2}>
                    <TextField
                      name="location.name"
                      label={t('locationName')}
                      value={formData.location.name}
                      onChange={handleInputChange}
                      fullWidth
                      required
                    />
                    <TextField
                      name="location.address"
                      label={t('address')}
                      value={formData.location.address}
                      onChange={handleInputChange}
                      fullWidth
                      required
                    />
                    <TextField
                      name="location.city"
                      label={t('city')}
                      value={formData.location.city}
                      onChange={handleInputChange}
                      fullWidth
                      required
                    />
                  </Stack>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.featured}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          featured: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={t('featured')}
                />
                <Box>
                  <input
                    accept="image/jpeg,image/jpg,image/png"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    onChange={handleImageChange}
                  />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <label htmlFor="image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<ImageIcon />}
                      >
                        {t('uploadImage')}
                      </Button>
                    </label>
                    <Button
                      variant="outlined"
                      startIcon={<PhotoLibraryIcon />}
                      onClick={() => setOpenImageDialog(true)}
                    >
                      {t('selectFromGallery')}
                    </Button>
                  </Stack>
                </Box>
                
                {/* Image Preview */}
                {(imagePreview || formData.imageUrl) && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <img
                      src={imagePreview || formData.imageUrl}
                      alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
                    />
                  </Box>
                )}
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
            <Button onClick={handleSubmit} variant="contained">
              {selectedActivity ? t('save') : t('create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Selection Dialog */}
        <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{t('selectImage')}</Typography>
              <IconButton onClick={() => setOpenImageDialog(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <ImageGalleryManager
              selectedUrl={formData.imageUrl || ''}
              onSelect={handleImageSelect}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenImageDialog(false)}>
              {t('cancel')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Participants Dialog */}
        <Dialog
          open={participantsDialog.open}
          onClose={handleCloseParticipantsDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {t('participantsList')}
              </Typography>
              <IconButton onClick={handleCloseParticipantsDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <ParticipantsList
              activity={participantsDialog.activity}
              participants={participantsDialog.participants}
              onParticipantUpdate={handleParticipantUpdate}
            />
          </DialogContent>
        </Dialog>

        {/* Activity Management Dialog */}
        <Dialog
          open={activityManagementDialog.open}
          onClose={handleCloseActivityManagement}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {activityManagementDialog.view === 'participants' ? t('participantsList') : t('editActivity')}
              </Typography>
              <Box>
                <Button
                  onClick={toggleActivityManagementView}
                  startIcon={activityManagementDialog.view === 'participants' ? <EditIcon /> : <VisibilityIcon />}
                  sx={{ mr: 1 }}
                >
                  {activityManagementDialog.view === 'participants' ? t('edit') : t('viewParticipants')}
                </Button>
                <IconButton 
                  onClick={() => handleActivityDelete(activityManagementDialog.activity)}
                  color="error"
                  sx={{ mr: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
                <IconButton onClick={handleCloseActivityManagement} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {activityManagementDialog.view === 'participants' ? (
              <ParticipantsList
                activity={activityManagementDialog.activity}
                participants={activityManagementDialog.participants}
                onParticipantUpdate={handleParticipantUpdate}
              />
            ) : (
              <Box component="form" sx={{ mt: 2 }}>
                <Stack spacing={3}>
                  <FormControl fullWidth>
                    <FormLabel>{t('category')}</FormLabel>
                    <TextField
                      select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      fullWidth
                    >
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>

                  <TextField
                    name="title"
                    label={t('title')}
                    value={formData.title}
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                  
                  <TextField
                    name="shortDescription"
                    label={t('shortDescription')}
                    value={formData.shortDescription}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={2}
                  />

                  <TextField
                    name="description"
                    label={t('description')}
                    value={formData.description}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={4}
                    required
                  />

                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Box sx={{ 
                        width: '100%',
                        '& .MuiPickersLayout-root': {
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }
                      }}>
                        <DatePicker
                          value={formData.date}
                          onChange={handleDateChange}
                          label={t('date')}
                          format="EEEE d MMMM"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              sx: {
                                '& .MuiInputLabel-root': {
                                  fontSize: { xs: '0.9rem', sm: '1rem' }
                                },
                                '& .MuiInputBase-input': {
                                  fontSize: { xs: '0.9rem', sm: '1rem' }
                                }
                              }
                            },
                            popper: {
                              sx: {
                                '& .MuiPickersDay-root': {
                                  fontSize: { xs: '0.9rem', sm: '1rem' }
                                },
                                '& .MuiTypography-root': {
                                  fontSize: { xs: '0.9rem', sm: '1rem' }
                                },
                                '& .MuiPickersCalendarHeader-label': {
                                  fontSize: { xs: '1rem', sm: '1.1rem' }
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                      <Stack spacing={2} sx={{ width: '100%' }}>
                        <TextField
                          label={t('time')}
                          type="time"
                          value={formData.date ? format(formData.date, 'HH:mm') : '08:00'}
                          onChange={handleTimeChange}
                          inputProps={{
                            step: 900,
                            min: '08:00',
                            max: '22:00'
                          }}
                          fullWidth
                          required
                          sx={{
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.9rem', sm: '1rem' }
                            },
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.9rem', sm: '1rem' }
                            }
                          }}
                        />
                      </Stack>
                    </Stack>
                  </LocalizationProvider>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    <TextField
                      name="duration"
                      label={t('duration')}
                      value={formData.duration}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      type="number"
                      helperText={t('durationHelp')}
                    />
                    {!activityManagementDialog.activity && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.repeatWeekly}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              repeatWeekly: e.target.checked
                            }))}
                          />
                        }
                        label={t('repeatWeekly')}
                        sx={{
                          minWidth: { xs: '100%', sm: '200px' },
                          ml: { xs: 0, sm: 2 },
                          '& .MuiFormControlLabel-label': {
                            fontSize: { xs: '0.9rem', sm: '1rem' }
                          }
                        }}
                      />
                    )}
                  </Stack>

                  <TextField
                    name="maxParticipants"
                    label={t('maxParticipants')}
                    value={formData.maxParticipants}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    type="number"
                  />

                  <FormControl fullWidth>
                    <FormLabel>{t('location')}</FormLabel>
                    <Stack spacing={2}>
                      <TextField
                        name="location.name"
                        label={t('locationName')}
                        value={formData.location.name}
                        onChange={handleInputChange}
                        fullWidth
                        required
                      />
                      <TextField
                        name="location.address"
                        label={t('address')}
                        value={formData.location.address}
                        onChange={handleInputChange}
                        fullWidth
                        required
                      />
                      <TextField
                        name="location.city"
                        label={t('city')}
                        value={formData.location.city}
                        onChange={handleInputChange}
                        fullWidth
                        required
                      />
                    </Stack>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.featured}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            featured: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={t('featured')}
                  />

                  <Box>
                    <input
                      accept="image/jpeg,image/jpg,image/png"
                      style={{ display: 'none' }}
                      id="image-upload"
                      type="file"
                      onChange={handleImageChange}
                    />
                    <Stack direction="row" spacing={2} alignItems="center">
                      <label htmlFor="image-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<ImageIcon />}
                        >
                          {t('uploadImage')}
                        </Button>
                      </label>
                      <Button
                        variant="outlined"
                        startIcon={<PhotoLibraryIcon />}
                        onClick={() => setOpenImageDialog(true)}
                      >
                        {t('selectFromGallery')}
                      </Button>
                    </Stack>
                  </Box>
                  
                  {/* Image Preview */}
                  {(imagePreview || formData.imageUrl) && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={imagePreview || formData.imageUrl}
                        alt="Preview"
                        style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>
            )}
          </DialogContent>
          {activityManagementDialog.view === 'edit' && (
            <DialogActions>
              <Button onClick={handleCloseActivityManagement}>{t('cancel')}</Button>
              <Button onClick={handleSubmit} variant="contained">
                {t('save')}
              </Button>
            </DialogActions>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default ActivityManagement; 